/**
 * Unified Agent API Service
 *
 * Single entry point for all AI agent communications.
 * Scene assistant agents share the MitRa `/api/v2/generate` request and response shape; routing picks the endpoint from agent config.
 */

import { get } from 'svelte/store';
import { user_data } from 'src/stores/account/user';
import type { AgentId } from '../components/AISceneChatbot/agents/agents.types';
import { getAgent, getAgentEndpoint } from '../components/AISceneChatbot/agents/agents.config';
import type { AIGenerateResponse, Option, AIIntent } from './ai-scene-chat-api';
import type { BrotherEyeOption } from './brothereye-chat-api';

export { resetBrotherEyeSession } from './brothereye-chat-api';

const PASSPHRASE_STORAGE = 'ai_admin_passphrase';

export interface AgentMessageOptions {
	newSession?: boolean;
}

export interface UnifiedAgentResponse {
	success: boolean;
	explanation: string;
	scene?: unknown | null;
	scenesCreated?: unknown[];
	devicesUsed?: string[];
	toolCallsCount?: number;
	intent?: AIIntent;
	options?: Option[] | BrotherEyeOption[];
	needsClarification?: boolean;
	usage?: {
		input_tokens: number;
		output_tokens: number;
		total_tokens: number;
		cost_usd: number;
		cache_write_tokens?: number;
		cache_read_tokens?: number;
		saved_usd?: number;
		cost_bgn?: number;
	};
	metadata?: {
		chosenSkill?: string;
		classification?: string;
		tier?: string;
		processingTime?: number;
		traceId?: string;
	};
}

function getAuthCredentials() {
	const userData = get(user_data);
	if (!userData || !userData.token) {
		throw new Error('User not authenticated');
	}
	return {
		jwt: userData.token,
		email: userData.email || '',
	};
}

function handleResponseError(response: Response): never {
	if (response.status === 401 || response.status === 403) {
		throw new Error('Authentication failed. Please check your credentials.');
	}
	if (response.status === 429) {
		throw new Error('Too many requests. Please wait a moment and try again.');
	}
	if (response.status === 500) {
		throw new Error('AI service error. Please try again later.');
	}
	if (response.status === 503) {
		throw new Error('AI service is temporarily unavailable. Please try again later.');
	}
	throw new Error(`Request failed: ${response.statusText}`);
}

function handleNetworkError(error: unknown): never {
	if (error instanceof TypeError && error.message.includes('fetch')) {
		throw new Error('Unable to connect to AI service. Please check your internet connection.');
	}
	throw new Error('Network error. Please try again.');
}

function getPassphrase(): string | undefined {
	try {
		return sessionStorage.getItem(PASSPHRASE_STORAGE) || undefined;
	} catch {
		return undefined;
	}
}

async function sendSceneAIMessage(
	agentId: AgentId,
	endpoint: string,
	prompt: string,
	conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
	options: AgentMessageOptions,
): Promise<UnifiedAgentResponse> {
	const credentials = getAuthCredentials();
	const passphrase = getPassphrase();

	let response: Response;

	try {
		response = await fetch(endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${credentials.jwt}`,
			},
			body: JSON.stringify({
				prompt,
				passphrase,
				email: credentials.email,
				conversationHistory: conversationHistory.map((msg) => ({
					role: msg.role,
					content: msg.content,
				})),
				newSession: options.newSession ?? false,
				agentId,
			}),
		});
	} catch (error) {
		handleNetworkError(error);
	}

	if (!response.ok) {
		handleResponseError(response);
	}

	try {
		const data: AIGenerateResponse = await response.json();
		return {
			success: data.success,
			explanation: data.explanation,
			scene: data.scene,
			scenesCreated: data.scenesCreated,
			devicesUsed: data.devicesUsed,
			toolCallsCount: data.toolCallsCount,
			intent: data.intent,
			options: data.options,
			usage: data.usage,
		};
	} catch {
		throw new Error('Invalid response from AI service.');
	}
}

/**
 * Send a message to the specified AI agent.
 * MitRa (scene-ai) and SoFi (brother-eye) both use the scene AI /generate API contract.
 */
export async function sendAgentMessage(
	agentId: AgentId,
	prompt: string,
	conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> = [],
	options: AgentMessageOptions = {},
): Promise<UnifiedAgentResponse> {
	getAgent(agentId);
	const endpoint = getAgentEndpoint(agentId);

	switch (agentId) {
		case 'scene-ai':
		case 'brother-eye':
			return sendSceneAIMessage(agentId, endpoint, prompt, conversationHistory, options);

		default: {
			const _exhaustive: never = agentId;
			throw new Error(`Unknown agent: ${_exhaustive}`);
		}
	}
}
